import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { useRecipes } from '../context/RecipeContext';
import { ROUTES } from '../utils/constants';
import { getRecipePageNumbers } from '../utils/pageNumbers';
import { BookContainer, BookNavigation } from '../components';
import RecipeLeftPage from '../components/RecipeLeftPage';
import RecipeRightPage from '../components/RecipeRightPage';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import IngredientCheckDialog, { IngredientStatus } from '../components/IngredientCheckDialog';
import { Recipe } from '../models/Recipe';

const RecipeDetailScreen: React.FC = () => {
  const navigate = useNavigate();
  const { id: recipeId } = useParams<{ id: string }>();
  const { 
    recipes,
    loading, 
    error, 
    getRecipeById, 
    deleteRecipe, 
    updateRecipe,
    setSelectedRecipe,
    generateRecipeSteps,
  } = useRecipes();
  
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [generatingSteps, setGeneratingSteps] = useState<boolean>(false);
  const [currentScale, setCurrentScale] = useState<number>(1);
  const [displayRecipe, setDisplayRecipe] = useState<Recipe | null>(null);
  const [modifiedRecipe, setModifiedRecipe] = useState<Recipe | null>(null);
  const [saveChangesDialogOpen, setSaveChangesDialogOpen] = useState<boolean>(false);
  const [authError, setAuthError] = useState<boolean>(false);
  const [ingredientCheckOpen, setIngredientCheckOpen] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_cookingContext, setCookingContext] = useState<{ ingredients: IngredientStatus[]; notes: string } | null>(null);
  const [ingredientCheckedStatus, setIngredientCheckedStatus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchRecipe = async () => {
      if (recipeId) {
        try {
          const fetchedRecipe = await getRecipeById(recipeId);
          if (fetchedRecipe) {
            // Initialize ingredient checked status (all checked by default)
            const initialCheckedStatus: Record<string, boolean> = {};
            fetchedRecipe.ingredients
              .split('\n')
              .map((ingredient: string) => ingredient.trim())
              .filter((ingredient: string) => ingredient)
              .forEach((ingredient: string) => {
                initialCheckedStatus[ingredient] = true;
              });

            // Batch state updates to reduce re-renders from 3 to 1
            React.startTransition(() => {
              setRecipe(fetchedRecipe);
              setDisplayRecipe(fetchedRecipe);
              setSelectedRecipe(fetchedRecipe);
              setAuthError(false);
              // Reset scale when loading a fresh recipe
              setCurrentScale(1);
              setIngredientCheckedStatus(initialCheckedStatus);
            });
          } else {
            // Recipe not found or auth issue
            setAuthError(true);
          }
        } catch (err: any) {
          console.error('Error fetching recipe:', err);
          // Check if it's a permission error
          if (err?.code === 'permission-denied' || err?.message?.includes('permission')) {
            setAuthError(true);
          } else {
            navigate(ROUTES.RECIPES);
          }
        }
      }
    };

    fetchRecipe();
    // NOTE: Removed setSelectedRecipe from dependencies to prevent refetch after scaling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId, getRecipeById, navigate]);

  const handleBack = () => {
    navigate(ROUTES.RECIPES);
  };

  const handleEdit = () => {
    if (recipe) {
      navigate(`/recipes/${recipe.id}/edit`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (recipeId) {
      await deleteRecipe(recipeId);
      setDeleteDialogOpen(false);
      navigate(ROUTES.RECIPES);
    }
  };

  const handleStartCooking = async () => {
    if (recipe) {
      // Check if there are ingredient modifications
      if (modifiedRecipe) {
        setSaveChangesDialogOpen(true);
        return;
      }

      // Show ingredient check dialog
      setIngredientCheckOpen(true);
    }
  };

  const handleIngredientCheckConfirm = async (ingredients: IngredientStatus[], notes: string) => {
    setIngredientCheckOpen(false);

    if (!recipe) {
      return;
    }

    // Store cooking context for potential use in chat
    setCookingContext({ ingredients, notes });

    const unavailable = ingredients.filter(i => !i.available);
    const hasCustomization = unavailable.length > 0 || notes.trim();

    const recipeToUse = displayRecipe || recipe;

    // If there are customizations, always regenerate steps with context
    if (hasCustomization) {
      try {
        setGeneratingSteps(true);
        // Build context string for step generation
        const contextNotes = [
          recipeToUse.notes || '',
          unavailable.length > 0
            ? `\n[MISSING INGREDIENTS: ${unavailable.map(i => i.ingredient).join(', ')}]`
            : '',
          notes.trim()
            ? `\n[USER REQUEST: ${notes.trim()}]`
            : '',
        ].join('');

        const recipeWithContext = {
          ...recipeToUse,
          notes: contextNotes,
          steps: [], // Force regeneration with new context
        };

        const updatedRecipe = await generateRecipeSteps(recipeWithContext);
        // Restore original notes but keep new steps
        updatedRecipe.notes = recipeToUse.notes;
        setRecipe(updatedRecipe);
        setSelectedRecipe(updatedRecipe);
        navigate(`/recipes/${recipeId}/steps/0`);
      } catch (error) {
        console.error('Error generating customized steps:', error);
      } finally {
        setGeneratingSteps(false);
      }
    } else {
      // No customization, proceed normally
      if (!recipeToUse.steps || recipeToUse.steps.length === 0) {
        try {
          setGeneratingSteps(true);
          const updatedRecipe = await generateRecipeSteps(recipeToUse);
          setRecipe(updatedRecipe);
          setSelectedRecipe(updatedRecipe);
          navigate(`/recipes/${recipeId}/steps/0`);
        } catch (error) {
          console.error('Error generating steps:', error);
        } finally {
          setGeneratingSteps(false);
        }
      } else {
        setSelectedRecipe(recipeToUse);
        navigate(`/recipes/${recipeId}/steps/0`);
      }
    }
  };

  const handleScaleChange = async (scaledRecipe: Recipe, scale: number) => {
    console.log('📐 Scale change handler called with scale:', scale);
    console.log('📐 Scaled recipe ingredients:', scaledRecipe.ingredients);
    console.log('📐 Scaled recipe servings:', scaledRecipe.servings);

    // Use React.startTransition to batch state updates for better performance
    React.startTransition(() => {
      setDisplayRecipe(scaledRecipe);
      setCurrentScale(scale);
      setSelectedRecipe(scaledRecipe);
      // Clear any manual modifications when scaling is applied
      setModifiedRecipe(null);
    });

    // Save the scaled recipe to Firestore
    try {
      await updateRecipe(scaledRecipe);
      console.log('✅ Scaled recipe saved to server');
    } catch (error) {
      console.error('❌ Failed to save scaled recipe:', error);
      // You may want to show a toast notification to the user here
    }
  };

  const handleIngredientsChange = (updatedIngredients: string[]) => {
    if (recipe) {
      const updatedRecipe: Recipe = {
        ...recipe,
        ingredients: updatedIngredients.join('\n')
      };
      setModifiedRecipe(updatedRecipe);
      setDisplayRecipe(updatedRecipe);
    }
  };

  const handleIngredientCheckedChange = (ingredient: string, checked: boolean) => {
    setIngredientCheckedStatus(prev => ({
      ...prev,
      [ingredient]: checked,
    }));
  };

  const handleSaveChangesForThisTime = async () => {
    setSaveChangesDialogOpen(false);
    if (modifiedRecipe) {
      const recipeToUse = modifiedRecipe;
      if (!recipeToUse.steps || recipeToUse.steps.length === 0) {
        try {
          setGeneratingSteps(true);
          const updatedRecipe = await generateRecipeSteps(recipeToUse);
          setSelectedRecipe(updatedRecipe);
          navigate(`/recipes/${recipeId}/steps/0`);
        } catch (error) {
          console.error('Error generating steps:', error);
        } finally {
          setGeneratingSteps(false);
        }
      } else {
        setSelectedRecipe(recipeToUse);
        navigate(`/recipes/${recipeId}/steps/0`);
      }
    }
  };

  const handleUpdateRecipe = async () => {
    setSaveChangesDialogOpen(false);
    if (modifiedRecipe) {
      try {
        const updatedRecipe = await updateRecipe(modifiedRecipe);
        setRecipe(updatedRecipe);
        setDisplayRecipe(updatedRecipe);
        setModifiedRecipe(null);
        
        const recipeToUse = updatedRecipe;
        if (!recipeToUse.steps || recipeToUse.steps.length === 0) {
          setGeneratingSteps(true);
          const recipeWithSteps = await generateRecipeSteps(recipeToUse);
          setSelectedRecipe(recipeWithSteps);
          navigate(`/recipes/${recipeId}/steps/0`);
          setGeneratingSteps(false);
        } else {
          setSelectedRecipe(recipeToUse);
          navigate(`/recipes/${recipeId}/steps/0`);
        }
      } catch (error) {
        console.error('Error updating recipe:', error);
        setGeneratingSteps(false);
      }
    }
  };


  if (loading || !recipe) {
    return (
      <BookContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </Box>
      </BookContainer>
    );
  }

  if (error) {
    return (
      <BookContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          {error}
        </Box>
      </BookContainer>
    );
  }

  const currentRecipe = displayRecipe || recipe;
  
  // Calculate page numbers
  const recipeIndex = recipes.findIndex(r => r.id === recipe.id);
  const pageNumbers = recipeIndex !== -1 ? getRecipePageNumbers(recipeIndex) : { left: 0, right: 0 };

  // Meta chips for hero
  const ingredientsList = currentRecipe!.ingredients
    .split('\n')
    .map((ingredient: string) => ingredient.trim())
    .filter((ingredient: string) => ingredient);

  const metaChips = [
    {
      icon: undefined,
      label: `${Math.max(15, Math.min(60, ingredientsList.length * 5))} min`,
    },
    (recipe.analysis && recipe.analysis.difficulty && recipe.analysis.difficulty.level) ? {
      icon: undefined,
      label: recipe.analysis.difficulty.level,
    } : undefined,
    (recipe.analysis && recipe.analysis.cuisine && recipe.analysis.cuisine.primary && recipe.analysis.cuisine.primary !== 'other') ? {
      icon: undefined,
      label: recipe.analysis.cuisine.primary,
    } : undefined,
    (recipe.analysis && recipe.analysis.dietary && Array.isArray(recipe.analysis.dietary.restrictions) && recipe.analysis.dietary.restrictions.length > 0) ? {
      icon: undefined,
      label: recipe.analysis.dietary.restrictions.join(', '),
    } : undefined,
  ].filter((chip) => chip !== undefined);

  // Show auth error dialog if user doesn't have permission
  if (authError) {
    return (
      <BookContainer>
        <Dialog open={true} onClose={() => navigate(ROUTES.RECIPES)}>
          <DialogTitle>Authentication Required</DialogTitle>
          <DialogContent>
            <Typography>
              You need to be logged in to view this recipe, or this recipe belongs to another user.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => navigate(ROUTES.RECIPES)} color="primary">
              Back to Recipes
            </Button>
          </DialogActions>
        </Dialog>
      </BookContainer>
    );
  }

  return (
    <BookContainer>

      <Box sx={{
        width: '100%',
        py: { xs: 2, md: 4 },
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'center',
          alignItems: 'stretch',
          gap: 0,
          maxWidth: { xs: '100%', md: 1200 },
          margin: '0 auto',
        }}>
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <RecipeLeftPage
              recipe={currentRecipe}
              currentScale={currentScale}
              onBack={handleBack}
              onScaleChange={handleScaleChange}
              onIngredientsChange={handleIngredientsChange}
              onStartCooking={handleStartCooking}
              generatingSteps={generatingSteps}
              metaChips={metaChips}
              pageNumber={pageNumbers.left}
              ingredientCheckedStatus={ingredientCheckedStatus}
              onIngredientCheckedChange={handleIngredientCheckedChange}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <RecipeRightPage
              recipe={currentRecipe}
              onEdit={handleEdit}
              onDelete={() => setDeleteDialogOpen(true)}
              pageNumber={pageNumbers.right}
            />
          </Box>
        </Box>
      </Box>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        title={recipe.title}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <Dialog
        open={saveChangesDialogOpen}
        onClose={() => setSaveChangesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Ingredient Changes?</DialogTitle>
        <DialogContent>
          <Typography>
            You've modified the ingredients for this recipe. Would you like to:
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              • <strong>Use for this time only:</strong> Keep changes for this cooking session without updating the recipe
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              • <strong>Update the recipe:</strong> Save changes permanently to the recipe
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveChangesDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveChangesForThisTime}
            variant="outlined"
          >
            Use for This Time
          </Button>
          <Button 
            onClick={handleUpdateRecipe}
            variant="contained"
          >
            Update Recipe
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Book Navigation */}
      <BookNavigation />

      {/* Ingredient Check Dialog */}
      <IngredientCheckDialog
        open={ingredientCheckOpen}
        onClose={() => setIngredientCheckOpen(false)}
        recipe={recipe}
        onConfirm={handleIngredientCheckConfirm}
        initialCheckedStatus={ingredientCheckedStatus}
      />
    </BookContainer>
  );
};

export default RecipeDetailScreen;
